import { useEffect, useState, useRef } from 'react'
import { useMapLayers } from '@/contexts/MapLayersContext'
import { usePondingLogs } from './use-ponding-logs'
import { PondingMarkers } from '@/components/map/ponding-markers'

/**
 * Hook to register ponding logs layer with the map context
 * Manages its own data fetching and provides a marker component
 */
export function usePondingLayer() {
  const { addLayer, updateLayer, toggleLayer, layers } = useMapLayers()
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const registeredRef = useRef(false)
  const prevDataRef = useRef<{ logs: any[], loading: boolean, eventId: string | null }>({
    logs: [],
    loading: false,
    eventId: null
  })
  
  // Fetch ponding data based on selected event
  const { pondingLogs, loading } = usePondingLogs(selectedEventId)

  const layerId = 'ponding-logs'

  // Register the ponding layer on mount (only once)
  useEffect(() => {
    if (registeredRef.current) return
    registeredRef.current = true
    
    addLayer({
      id: layerId,
      name: 'Ponding Logs',
      type: 'marker',
      visible: true,
      zIndex: 600,
      component: PondingMarkers,
      componentProps: {
        pondingLogs: [],
        visible: true,
      },
      metadata: {
        selectedEventId: null,
        loading: false,
      },
    })
  }, [addLayer])

  // Update component props when data actually changes (not on every render)
  useEffect(() => {
    const prev = prevDataRef.current
    const hasChanged = 
      prev.logs !== pondingLogs ||
      prev.loading !== loading ||
      prev.eventId !== selectedEventId
    
    if (!hasChanged || !registeredRef.current) return
    
    prevDataRef.current = { logs: pondingLogs, loading, eventId: selectedEventId }
    
    updateLayer(layerId, {
      componentProps: {
        pondingLogs,
        visible: true, // Will be overridden by toggle
      },
      metadata: {
        selectedEventId,
        loading,
      },
    })
  }, [pondingLogs, loading, selectedEventId, updateLayer])

  const pondingLayer = layers.find(l => l.id === layerId)

  return {
    layer: pondingLayer,
    toggleVisibility: () => toggleLayer(layerId),
    setEventFilter: setSelectedEventId,
    selectedEventId,
    loading,
  }
}
